import { Spin } from '../../Ant';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useGetMatterOrInvoiceTransactions } from '../../../hooks/useGetMatterOrInvoiceTransactions';
import { EntriesTransactionFilters, EntryEntity } from '../../../constants/entries';
import TransactionCard from '../../dashboard/transactionCard';
import NoData from '../../dashboard/noData';
import { DocumentTextEmpty } from '../../icons';
import { useTranslation } from 'react-i18next';
import SearchInputWithFilters from '../../dashboard/SearchInputWithFilters';
import { IFilterOption, ITransaction } from '../../../interface/transaction';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../context/UserContext';
import { TransactionStatus } from '../../../constants/transaction';
import { groupTransactionsByDay } from '../../../constants/transactionUtility';
import { ROUTES, UserType } from '../../../constants/constants';
import RenderTransactionRestrictions from '../../dashboard/addTransaction/renderTransactionRestrictions';
import dayjs from 'dayjs';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useTransaction } from '../../../context/TransactionContext';
import { IMatter } from '../../../interface';
import { useCopyTransaction } from '../../../hooks/useCopyTransaction';
import { getLocalDateAndTime } from '../../../constants/utilityFunctions';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import { TRANSACTION_CARD_ACTION_COUNT } from '../../common/skeleton/skeletonTransactionList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../../hooks/useFetchAllPages';

const EntriesTransactionListing = ({
  entity,
  invoicingPeriod,
  matter,
  isBillableMatter,
}: {
  entity: string;
  matter?: IMatter;
  invoicingPeriod?: (string | null)[];
  isBillableMatter?: boolean;
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useUser();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const initialSearch = searchParams.get('transactionSearch') || '';
  const initialViewFilterValue = searchParams.get('transactionViewFilter') || '';
  const initialAuthorFilterValue = searchParams.get('transactionAuthorFilter') || '';
  const initialAuthorFilterValues = initialAuthorFilterValue
    ? initialAuthorFilterValue.split(',')
    : [];
  const multipleFilters: IFilterOption[] = useMemo(() => {
    return [
      {
        label: t('transaction:filter.myOwn'),
        value: EntriesTransactionFilters.MY,
      },
      {
        label: t('transaction:filter.otherAuthors'),
        value: EntriesTransactionFilters.OTHER,
      },
    ];
  }, [t]);

  const uniqueFilters: IFilterOption[] = useMemo(() => {
    return [
      {
        label: t('transaction:filter.allTransactions'),
        value: EntriesTransactionFilters.ALL_TRANSACTIONS,
        filterVisibleWhenSelected: false,
      },
      {
        label: t('transaction:filter.openTransactions'),
        value: EntriesTransactionFilters.OPEN_TRANSACTIONS,
      },
    ];
  }, [t]);
  const initialSelectedAuthorFilter = matter?.showOnlyOwnEntries
    ? multipleFilters.filter(
        (opt) =>
          opt.value === EntriesTransactionFilters.MY ||
          initialAuthorFilterValues.includes(opt.value),
      )
    : multipleFilters.filter((opt) => initialAuthorFilterValues.includes(opt.value));
  const initialSelectedViewFilter = uniqueFilters.find(
    (opt) => opt.value === initialViewFilterValue,
  );
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true || user?.userType === UserType.RESTRICTED;
  const queryClient = useQueryClient();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [isNewTransactionButton, setIsNewTransactionButton] = useState<boolean>(false);
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedViewFilter, setSelectedViewFilter] = useState<IFilterOption | undefined>(
    initialSelectedViewFilter,
  );
  const [selectedAuthorFilter, setSelectedAuthorFilter] = useState<IFilterOption[] | undefined>(
    initialSelectedAuthorFilter,
  );
  const handleAuthorFilter = () => {
    if (!selectedAuthorFilter) {
      return undefined;
    }

    if (
      selectedAuthorFilter.some(
        (val) =>
          val.value.includes(EntriesTransactionFilters.MY) &&
          selectedAuthorFilter.some((value) =>
            value.value.includes(EntriesTransactionFilters.OTHER),
          ),
      )
    ) {
      return undefined;
    } else if (
      selectedAuthorFilter.some((val) => val.value.includes(EntriesTransactionFilters.MY))
    ) {
      return user?.id;
    } else if (
      selectedAuthorFilter.some((val) => val.value.includes(EntriesTransactionFilters.OTHER))
    ) {
      return `$not:${user?.id}`;
    }
    return undefined;
  };
  const handleStatusFilter = () => {
    if (entity !== EntryEntity.MATTER) return;
    if (selectedViewFilter?.value.includes(EntriesTransactionFilters.OPEN_TRANSACTIONS)) {
      return TransactionStatus.open;
    }

    // If not billable matter and not filtering for open transactions, don't filter by status
    if (!isBillableMatter) {
      return undefined;
    }

    return `$in:${TransactionStatus.open},${TransactionStatus.reviewed},${TransactionStatus.preliminaryInvoice}`;
  };
  const statusFilter = handleStatusFilter();
  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    data: paginatedTransactionData,
    isLoading: isLoadingTransactions,
  } = useGetMatterOrInvoiceTransactions({
    id,
    type: entity,
    limit: 50,
    search: searchValue,
    sortBy: ['transactionDate:DESC'],
    relations: [
      'unit:translations',
      'matter:currency,subject,matterNumber,language,invoiceableType',
      'transactionUser:fullName',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'transactionType',
    ],
    ...(entity === EntryEntity.INVOICE ? { 'filter.preliminaryInvoiceId': `${id}` } : {}),
    extraFilters: {
      transactionUserId: handleAuthorFilter(),
      ...(entity === EntryEntity.MATTER && statusFilter ? { status: statusFilter } : {}),
      ...(invoicingPeriod?.length &&
      invoicingPeriod[0] != null &&
      invoicingPeriod[1] != null &&
      invoicingPeriod[0] !== invoicingPeriod[1]
        ? {
            transactionDate: `$btw:${dayjs(invoicingPeriod[0]).format('YYYY-MM-DD')},${dayjs(
              invoicingPeriod[1],
            ).format('YYYY-MM-DD')}`,
          }
        : {}),
    },
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'unitPrice',
      'unitId',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'hasNotes',
      'hasImportantNotes',
      'matterId',
      'workedHours',
    ],
  });
  const groupedTransactions = useMemo(
    () => groupTransactionsByDay(paginatedTransactionData),
    [paginatedTransactionData],
  );
  const { isCollapsed, toggle, getToggleAllControl } = useCollapsedDateGroups(
    `${DateGroupList.matterTransactions}:${id}`,
  );
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (isNewTransactionButton && transaction) {
      updateTransaction({
        ...transaction,
        overrideWarnings: true,
        principalCompanyId: matter?.principalCompanyId,
        principalPrivatePersonId: matter?.principalPrivatePersonId,
        principalCompany: matter?.principalCompany,
        principalPrivatePerson: matter?.principalPrivatePerson,
      } as ITransaction);
      push(ROUTES.ADD_EDIT_TRANSACTION);
      return;
    }
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setCopyClicked(false);
      updateTransaction(null);
      setSelectedTransaction(undefined);
    }
  };
  const handleAddTransaction = () => {
    const transaction = {
      matter: matter,
      matterId: matter?.id,
      transactionDate: dayjs().format('YYYY-MM-DD'),
    } as unknown as ITransaction;
    setIsNewTransactionButton(true);
    setSelectedTransaction(transaction);
  };
  useEffect(() => {
    setSearchParams({
      transactionSearch: searchValue,
      transactionViewFilter: selectedViewFilter?.value || '',
      transactionAuthorFilter: selectedAuthorFilter?.map((f) => f.value).join(',') || '',
    });
  }, [searchValue, selectedViewFilter, selectedAuthorFilter]);
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="entries-transaction-listing"
      contentGap="gutter"
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(
            groupedTransactions.map((group) => toDateGroupKey(group?.date)),
          )}
          searchTitle={t('transaction:search')}
          filterTitle={t('transaction:filter.label')}
          searchValue={searchValue}
          checkboxOptions={multipleFilters}
          radioOptions={uniqueFilters}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['matter-invoice-transactions'] });
            return refetch();
          }}
          radioTitle={t('common:view')}
          checkboxTitle={t('common:author')}
          selectedFilter={Array.from(
            new Map(
              [
                ...(selectedViewFilter ? [selectedViewFilter] : []),
                ...(selectedAuthorFilter ?? []),
              ].map((item) => [item.value, item]),
            ).values(),
          )}
          setSelectedFilter={(val) => setSelectedViewFilter(val)}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            if (matter?.showOnlyOwnEntries) {
              const myFilter = multipleFilters.find(
                (f) => f.value === EntriesTransactionFilters.MY,
              );
              setSelectedAuthorFilter(myFilter ? [myFilter] : val);
            } else {
              setSelectedAuthorFilter(val);
            }
          }}
          handleAdd={entity === EntryEntity.INVOICE ? undefined : handleAddTransaction}
          tooltipTextForAdd={t('transaction:addTransaction')}
          clearFilter={(val?: string) => {
            if (!val) {
              setSelectedViewFilter(undefined);
              if (matter?.showOnlyOwnEntries) {
                const myFilter = multipleFilters.find(
                  (f) => f.value === EntriesTransactionFilters.MY,
                );
                setSelectedAuthorFilter(myFilter ? [myFilter] : undefined);
              } else {
                setSelectedAuthorFilter(undefined);
              }
              return;
            }
            if (val) {
              setSelectedAuthorFilter((prev) => (prev ?? []).filter((item) => item.value !== val));
            }
            if (selectedViewFilter?.value === val) {
              setSelectedViewFilter(undefined);
            }
          }}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          disabledCheckboxValues={
            matter?.showOnlyOwnEntries
              ? [EntriesTransactionFilters.OTHER, EntriesTransactionFilters.MY]
              : []
          }
          filtersDisabledInfoMessage={
            matter?.showOnlyOwnEntries
              ? t('transaction:showOnlyOwnEntriesFilterLockedMessage')
              : undefined
          }
        />
      }
    >
      {groupedTransactions && Object.entries(groupedTransactions).length > 0 && (
        <>
          {groupedTransactions.map((data) => {
            const groupKey = toDateGroupKey(data?.date);
            return (
              <CollapsibleDateGroup
                key={groupKey}
                title={data?.date ? getLocalDateAndTime(new Date(data.date), true) : ''}
                itemCount={t('transaction:transactionCount', { count: data?.transactions.length })}
                collapsed={isCollapsed(groupKey)}
                onToggle={() => toggle(groupKey)}
              >
                {data?.transactions.map((transaction) => (
                  <TransactionCard
                    data-testid="transaction-card"
                    childListCard
                    key={transaction.id}
                    transaction={transaction}
                    hidden={hidePricingInfo}
                    onEdit={(transaction) => {
                      setSelectedTransaction(transaction);
                    }}
                    onCopy={(transaction) => {
                      handleCopyTransaction(transaction);
                    }}
                    onNewToSameMatter={startTransactionOnSameMatter}
                  />
                ))}
              </CollapsibleDateGroup>
            );
          })}
        </>
      )}
      {(!paginatedTransactionData ||
        (Object.entries(paginatedTransactionData).length === 0 && !isFetching)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('transaction:noTransactions')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<DocumentTextEmpty />}
          />
        </div>
      )}
      {isLoadingTransactions ? (
        <SkeletonCardList
          rows={[1, 1, 2]}
          withSubcontentOnFirstRow
          actionCount={TRANSACTION_CARD_ACTION_COUNT}
        />
      ) : (
        renderLoader()
      )}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </CollapsibleListLayout>
  );
};

export default EntriesTransactionListing;
